import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, Platform, StyleSheet, View } from 'react-native';
// The web build takes icons from components/Icons (inline SVG); lucide-react-native would pull in react-native-svg.
import { Pointer } from 'components/Icons';
import { useRemScale } from 'lib/remScale';
import { findTarget, isOnScreen } from '../targets';

const LIME = '#D7F46E';
const HAND = 48;
const TILT = -25; // degrees: lucide's Pointer points straight up; tilted to point up-left like a mouse cursor
const INSIDE = 6; // the fingertip sits this far inside the button's bottom-right corner
// Where the fingertip is inside the hand's box: lucide's index finger tip (8, 2) of 24, turned by TILT around the centre;
// the icon is drawn 6 px smaller than the box, centred.
const TIP = (() => {
  const a = (TILT * Math.PI) / 180;
  const fx = 12 + -4 * Math.cos(a) - -10 * Math.sin(a);
  const fy = 12 + -4 * Math.sin(a) + -10 * Math.cos(a);
  const icon = HAND - 6;
  return { x: 3 + (fx / 24) * icon, y: 3 + (fy / 24) * icon };
})();

/** Where the hand's box goes for a target box: fingertip on its bottom-right corner, kept on screen. */
export function handPlace(rect, viewWidth, viewHeight) {
  return {
    left: Math.min(rect.left + rect.width - INSIDE - TIP.x, viewWidth - HAND - 4),
    top: Math.min(rect.top + rect.height - INSIDE - TIP.y, viewHeight - HAND - 4),
  };
}

/**
 * The one thing to click (the owner's prototype, rounds 9 Oct - 14): a pulsing green outline with a glow around the
 * target, and a pulsing green hand that stays in the same place on every button - like a mouse cursor, its fingertip on
 * the button's bottom-right corner, pointing up-left (owner round 14.1: "consistent and stay at one place for any
 * button"; Sol round14-hand2 B), so the label stays readable and the hand never points at a neighbour. Follows the
 * target while it moves or resizes. Never takes clicks - the target underneath is pressed as usual.
 */
export default function FocusRing({ targetId, onMissing, hand = true }) {
  const { rem } = useRemScale();
  const [rect, setRect] = useState(null);
  const pulse = useRef(new Animated.Value(0)).current;
  const missingSince = useRef(null);

  useEffect(() => {
    if (Platform.OS !== 'web' || !targetId) return undefined;
    let frame = 0;
    let last = '';
    const measure = () => {
      const element = findTarget(targetId);
      const box = element ? element.getBoundingClientRect() : null;
      const visible = isOnScreen(box, window.innerWidth, window.innerHeight);
      const key = visible ? `${box.left}|${box.top}|${box.width}|${box.height}|${window.innerWidth}|${window.innerHeight}` : '';
      if (key !== last) {
        last = key;
        setRect(visible ? { left: box.left, top: box.top, width: box.width, height: box.height } : null);
      }
      if (!visible) {
        missingSince.current = missingSince.current || Date.now();
        // A target hidden in a collapsed panel: let the tour open it.
        if (onMissing && Date.now() - missingSince.current > 400) {
          missingSince.current = Date.now();
          onMissing(targetId);
        }
      } else {
        missingSince.current = null;
      }
      frame = requestAnimationFrame(measure);
    };
    frame = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(frame);
  }, [onMissing, targetId]);

  useEffect(() => {
    const loop = Animated.loop(
      Animated.timing(pulse, { toValue: 1, duration: 1100, easing: Easing.inOut(Easing.quad), useNativeDriver: false }),
    );
    loop.start();
    return () => loop.stop();
  }, [pulse]);

  if (!rect) return null;
  const pad = 8;
  const ripple = pulse.interpolate({ inputRange: [0, 1], outputRange: [0, rem(14)] });
  const rippleOpacity = pulse.interpolate({ inputRange: [0, 1], outputRange: [0.75, 0] });
  const glow = pulse.interpolate({ inputRange: [0, 0.5, 1], outputRange: [0.6, 1, 0.6] });
  const nudge = pulse.interpolate({ inputRange: [0, 0.5, 1], outputRange: [0, -5, 0] });
  const at = handPlace(rect, window.innerWidth, window.innerHeight);
  return (
    <View pointerEvents="none" style={styles.layer} testID="first-visit-focus">
      <Animated.View
        style={[
          styles.outline,
          {
            left: rect.left - pad,
            top: rect.top - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
            borderRadius: 16,
            borderWidth: 3,
            opacity: glow,
          },
        ]}
      />
      <Animated.View
        style={[
          styles.ripple,
          {
            left: Animated.subtract(rect.left - pad, ripple),
            top: Animated.subtract(rect.top - pad, ripple),
            width: Animated.add(rect.width + pad * 2, Animated.multiply(ripple, 2)),
            height: Animated.add(rect.height + pad * 2, Animated.multiply(ripple, 2)),
            borderRadius: 20,
            borderWidth: 2,
            opacity: rippleOpacity,
          },
        ]}
      />
      {hand ? (
        <Animated.View
          testID="first-visit-hand"
          style={[styles.hand, { left: at.left, top: at.top, width: HAND, height: HAND, transform: [{ translateX: nudge }, { translateY: nudge }] }]}>
          <View style={[styles.handGlow, { borderRadius: HAND / 2 }]} />
          <View style={{ transform: [{ rotate: `${TILT}deg` }] }}>
            <Pointer size={HAND - 6} color={LIME} fill="rgba(197,232,54,0.55)" strokeWidth={2.4} />
          </View>
        </Animated.View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  layer: { position: 'fixed', left: 0, top: 0, right: 0, bottom: 0, zIndex: 120 },
  outline: {
    position: 'absolute',
    borderColor: LIME,
    backgroundColor: 'rgba(197,232,54,0.06)',
    shadowColor: 'rgb(197,232,54)',
    shadowOpacity: 0.8,
    shadowRadius: 22,
    shadowOffset: { width: 0, height: 0 },
  },
  ripple: { position: 'absolute', borderColor: LIME },
  hand: { position: 'absolute', alignItems: 'center', justifyContent: 'center' },
  handGlow: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(197,232,54,0.18)',
    shadowColor: 'rgb(197,232,54)',
    shadowOpacity: 0.7,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 0 },
  },
});
